<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import clsx from 'clsx';

const props = defineProps<{
  href?: string;
  current?: boolean;
  class?: string;
}>();

const route = useRoute();

const isCurrent = computed(() => {
  if (typeof props.current === 'boolean') return props.current;
  if (!props.href) return false;
  if (props.href === '/') return route.path === '/';
  return route.path === props.href || route.path.startsWith(props.href + '/');
});

const itemClass = clsx(
  'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-zinc-950 sm:py-2 sm:text-sm/5',
  '[&>svg]:size-6 [&>svg]:shrink-0 [&>svg]:text-zinc-500 sm:[&>svg]:size-5',
  'hover:bg-zinc-950/5 hover:[&>svg]:text-zinc-950',
  'aria-[current=page]:bg-zinc-950/5 aria-[current=page]:[&>svg]:text-zinc-950',
  'dark:text-white dark:[&>svg]:text-zinc-400',
  'dark:hover:bg-white/5 dark:hover:[&>svg]:text-white',
  'dark:aria-[current=page]:bg-white/5 dark:aria-[current=page]:[&>svg]:text-white',
);

const wrapperClass = clsx('relative', props.class);
</script>

<template>
  <span :class="wrapperClass">
    <span
      v-if="isCurrent"
      aria-hidden="true"
      class="absolute inset-y-1 -left-4 w-0.5 rounded-full bg-zinc-950 dark:bg-white"
    />
    <RouterLink
      v-if="href"
      :to="href"
      :class="itemClass"
      :aria-current="isCurrent ? 'page' : undefined"
    >
      <slot />
    </RouterLink>
    <button v-else type="button" :class="itemClass">
      <slot />
    </button>
  </span>
</template>
